// ----------------------------------------------------------------------------
// Enhanced line-item quantity picker.
//
// The cart line item uses a custom quantity picker (.line-item__quantity-picker)
// with explicit minus/plus buttons (data-action="decrease|increase"). The stock
// `line-item-quantity` element in theme.js only reacts to the input's `change`
// event - it has no handling for those buttons.
//
// This file registers `line-item-quantity` with a class that DOES handle the
// buttons (and the remove link), and it is loaded as a module BEFORE theme.js so
// it wins the customElements.define() race. theme.js guards its own definition
// with `if (!customElements.get('line-item-quantity'))`, so it will skip and this
// enhanced version stays in place on every page and after every AJAX cart render.
//
// Previously this lived in an inline <script> inside snippets/line-item.liquid.
// That only executed when a line item happened to be present in the initial
// server-rendered HTML; when the drawer was rebuilt via AJAX (empty cart then
// add, returning from checkout, etc.) the inline script never ran, the stock
// version had already claimed the element name, and the +/- buttons went dead.
// ----------------------------------------------------------------------------

class LineItemQuantityEnhanced extends HTMLElement {
  constructor() {
    super();
    this._onButtonClick = this._onButtonClick.bind(this);
    this._onQuantityChanged = this._onQuantityChanged.bind(this);
    this._onRemoveLinkClicked = this._onRemoveLinkClicked.bind(this);
  }

  connectedCallback() {
    this.inputElement = this.querySelector('input[data-line-key]');
    this.minusButton = this.querySelector('[data-action="decrease"]');
    this.plusButton = this.querySelector('[data-action="increase"]');

    if (this.minusButton) {
      this.minusButton.addEventListener('click', this._onButtonClick);
    }
    if (this.plusButton) {
      this.plusButton.addEventListener('click', this._onButtonClick);
    }
    if (this.inputElement) {
      this.inputElement.addEventListener('change', this._onQuantityChanged);
    }

    // Handle remove link clicks (bubbling from the <a>).
    this.addEventListener('click', this._onRemoveLinkClicked);

    this._updateButtonStates();
  }

  disconnectedCallback() {
    if (this.minusButton) {
      this.minusButton.removeEventListener('click', this._onButtonClick);
    }
    if (this.plusButton) {
      this.plusButton.removeEventListener('click', this._onButtonClick);
    }
    if (this.inputElement) {
      this.inputElement.removeEventListener('change', this._onQuantityChanged);
    }
  }

  _onButtonClick(event) {
    event.preventDefault();
    if (!this.inputElement) return;

    const action = event.currentTarget.dataset.action;
    const currentValue = parseInt(this.inputElement.value) || 1;
    const min = parseInt(this.inputElement.min) || 1;
    const max = this.inputElement.max ? parseInt(this.inputElement.max) : Infinity;
    const step = parseInt(this.inputElement.step) || 1;

    let newValue = currentValue;

    if (action === 'decrease') {
      newValue = Math.max(min, currentValue - step);
      // If already at the minimum, decreasing removes the item from the cart.
      if (currentValue <= min) {
        newValue = 0;
      }
    } else if (action === 'increase') {
      newValue = Math.min(max, currentValue + step);
    }

    if (newValue !== currentValue) {
      this.inputElement.value = newValue;
      this._changeLineItemQuantity(this.inputElement.dataset.lineKey, newValue);
    }
  }

  _onQuantityChanged(event) {
    if (!event.target.hasAttribute('data-line-key')) {
      return;
    }
    this._changeLineItemQuantity(event.target.dataset.lineKey, parseInt(event.target.value));
  }

  _onRemoveLinkClicked(event) {
    const link = event.target.closest ? event.target.closest('a') : null;
    if (!link || !link.href || !link.href.includes('/cart/change')) {
      return;
    }
    event.preventDefault();
    const url = new URL(link.href);
    // Prefer the key on our own quantity input: it is kept up to date by
    // shipping-property-sync when a property patch re-keys the line, while the
    // server-rendered href still carries the original (now dead) line number.
    const lineKey =
      (this.inputElement && this.inputElement.dataset.lineKey) ||
      url.searchParams.get('id') ||
      url.searchParams.get('line');
    const quantity = parseInt(url.searchParams.get('quantity'));
    this._changeLineItemQuantity(lineKey, isNaN(quantity) ? 0 : quantity);
  }

  // ---------------------------------------------------------------------------
  // A line key stops being valid as soon as anything rewrites that line's
  // properties (the delivery-date sync, a bundle app, an upsell app). Shopify
  // then answers /cart/change.js with 422 "geen geldige ID of regelnummer".
  // Rather than show that to the customer, re-resolve the line against a fresh
  // cart and retry once.
  // ---------------------------------------------------------------------------
  async _resolveFreshLineKey(staleKey) {
    try {
      const cart = await fetch(`${Shopify.routes.root}cart.js`, { cache: 'no-store' }).then((res) => res.json());

      // Still valid: the failure was not a stale key, so do not retry.
      if (cart.items.some((item) => item.key === String(staleKey))) return null;

      const variantId = String(staleKey).split(':')[0];
      const candidates = cart.items.filter((item) => String(item.variant_id) === variantId);
      if (candidates.length === 1) return candidates[0].key;
      if (candidates.length === 0) return null;

      // Several lines of the same variant: match on this line's position among
      // the rendered lines of that variant.
      const rendered = Array.from(document.querySelectorAll('input[data-line-key]')).filter(
        (input) => String(input.dataset.lineKey).split(':')[0] === variantId
      );
      const position = rendered.indexOf(this.inputElement);
      return candidates[position > -1 ? Math.min(position, candidates.length - 1) : 0].key;
    } catch (error) {
      return null;
    }
  }

  _syncLineKey(newKey) {
    const oldKey = this.inputElement && this.inputElement.dataset.lineKey;
    if (!newKey || !oldKey || oldKey === newKey) return;
    const escaped = window.CSS && window.CSS.escape ? window.CSS.escape(oldKey) : oldKey;
    document
      .querySelectorAll(`[data-line-key="${escaped}"], [line-key="${escaped}"]`)
      .forEach((el) => {
        if (el.hasAttribute('data-line-key')) el.setAttribute('data-line-key', newKey);
        if (el.hasAttribute('line-key')) el.setAttribute('line-key', newKey);
      });
  }

  _updateButtonStates() {
    if (!this.inputElement) return;

    const currentValue = parseInt(this.inputElement.value) || 1;
    const max = this.inputElement.max ? parseInt(this.inputElement.max) : Infinity;

    // Minus is never disabled (clicking at the minimum removes the item).
    // Plus is disabled once the maximum is reached.
    if (this.plusButton) {
      this.plusButton.disabled = currentValue >= max;
    }
  }

  async _changeLineItemQuantity(lineKey, targetQuantity) {
    // eslint-disable-next-line no-param-reassign
    const lineItem = this.closest('line-item');
    lineItem?.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity } }));

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const post = (key) =>
      fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: targetQuantity,
          sections: sectionsToBundle
        })
      });

    let response = await post(lineKey);

    if (!response.ok) {
      const freshKey = await this._resolveFreshLineKey(lineKey);
      if (freshKey) {
        this._syncLineKey(freshKey);
        lineKey = freshKey;
        response = await post(freshKey);
      }
    }

    if (!response.ok) {
      const responseContent = await response.json();
      this.closest('.line-item, tr')?.querySelector('[role="alert"]')?.remove();
      const errorSvg = `<svg width="14" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>`;
      this.closest('.line-item, tr')?.querySelector('.line-item__info')?.insertAdjacentHTML('beforeend', `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${responseContent['description']}</p>`);
      this.inputElement.value = this.inputElement.defaultValue;
      lineItem?.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
    } else {
      const cartContent = await response.json();

      if (window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
      } else {
        const lineItemAfterChange = cartContent['items'].filter((item) => item['key'] === lineKey);
        lineItem?.dispatchEvent(new CustomEvent('line-item:change', {
          bubbles: true,
          detail: {
            quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]['quantity'],
            cart: cartContent
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart: cartContent
          }
        }));
      }
    }

    this._updateButtonStates();
  }
}

// Register before theme.js so the enhanced (button-aware) version wins.
if (!window.customElements.get('line-item-quantity')) {
  window.customElements.define('line-item-quantity', LineItemQuantityEnhanced);
}